import { Fragment, useContext, useEffect, useMemo, useRef, useState } from "react";
import { Box, Chip, Grid } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import withObservables from "@nozbe/with-observables";
import {
  CalcInput,
  ParagraphText,
  UBButton,
  UBInput,
  UBRadioBox,
} from "../../../../Libraries";
import {
  AppCollectPayment,
  FileAttachment,
  TitleAndActionBox,
} from "../../../common";
import TabAutoComplete from "../../../../Libraries/TabAutoComplete";
import AddCustomerModal from "../../../common/Modals/AddCustomerModal";
import BusinessLoader from "../../../common/BusinessLoader";
import PaymentQRCode from "../../SaleExpense/PaymentQRCode";
import { LanguageContext } from "../../../../context/LanguageContext";
import { useAuthContext } from "../../../../context/AuthContext";
import { useWalletContext } from "../../../../context/WalletContext";
import { useSnackbar } from "../../../../context/SnackbarContext";
import { analytics, triggerLogEvent } from "../../../../config/firebase";
import { Sync } from "../../../../SyncService";
import { formattedCount, getCategoriesByType, resolveCategoryDisplayName } from "../../../../utils";
import { getCashTxnByID } from "../../../../database/helpers/cash_txn";
import { getCustomerOfBusiness, findCustomerByID } from "../../../../database/helpers/customer";
import { phoneNumRegex } from "../../../../validations";
import { EMIONBOARDINGSTATUS, VERIFIED } from "../../../../constants";
import styles from "../../SaleExpense/SaleExpenseForm/styles.module.css";

const getMoneyInPaymentOptions = (I18n) => [
  { name: "cash", display_name: I18n.cash, type: "cash" },
  { name: "qr", display_name: "QR", type: "qr" },
];

const getMoneyOutPaymentOptions = (I18n) => [
  { name: "cash", display_name: I18n.cash, type: "cash" },
  { name: "bank", display_name: I18n.bank, type: "bank" },
];

const CashTxnForm = ({ type, customers = [] }) => {
  const isMoneyIn = type === "in";

  const { fetchAccountInfo, accountInfo, isAccountLoading } = useWalletContext();
  const [I18n] = useContext(LanguageContext);
  const paymentOptions = isMoneyIn ? getMoneyInPaymentOptions(I18n) : getMoneyOutPaymentOptions(I18n);
  const { user, business } = useAuthContext();
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const inputAmountRef = useRef();

  const isEMIVerified =
    accountInfo?.emi_onboarding_status === EMIONBOARDINGSTATUS.VERIFIED;

  const [isLoading, setIsLoading] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(isEditMode);
  const [cashTxnDetail, setCashTxnDetail] = useState(null);
  const [categoryList, setCategoryList] = useState([]);
  const [categoryOpen, setCategoryOpen] = useState(false);
  const [addContact, setAddContact] = useState(false);
  const [createCustomerModal, setCreateCustomerModal] = useState(false);
  const [initName, setInitname] = useState("");
  const [initPhone, setInitphone] = useState("");
  const [files, setFiles] = useState([]);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(false);

  const resolvePaymentMethod = (method) => {
    if (!method) return paymentOptions[0];
    return (
      paymentOptions.find((m) => m.type === method || m.name === method) ||
      paymentOptions[0]
    );
  };

  const [state, setState] = useState(() => ({
    amount: "",
    date: moment(),
    note: "",
    category: "",
    categoryLabel: "",
    customer: null,
    paymentMethod: resolvePaymentMethod(null),
  }));

  useEffect(() => {
    triggerLogEvent(analytics, "cash_book_form_opened", { type, is_edit_mode: isEditMode });
  }, []);

  useEffect(() => {
    setCategoryList(
      getCategoriesByType(isMoneyIn ? "cb_cash_in" : "cb_cash_out"),
    );
  }, [isMoneyIn]);

  useEffect(() => {
    if (!accountInfo && user?.phone_number) fetchAccountInfo(navigate);
    if (inputAmountRef?.current) inputAmountRef.current.focus();
  }, [user]);

  useEffect(() => {
    if (!id) return;
    let isMounted = true;
    (async () => {
      try {
        const record = await getCashTxnByID(id);
        const cust = await record.customer.fetch().catch(() => null);
        if (!isMounted) return;
        setCashTxnDetail(record);
        setState({
          amount: record.amount != null ? String(record.amount) : "",
          date: record.transacted_at ? moment(record.transacted_at) : moment(),
          note: record.note || "",
          category: record.category || "",
          categoryLabel:
            resolveCategoryDisplayName(
              isMoneyIn ? "cb_cash_in" : "cb_cash_out",
              record.category || "",
            ) || "",
          customer: cust
            ? {
              id: cust.id,
              label: cust.name,
              labelText: cust.name,
              value: cust.name,
            }
            : null,
          paymentMethod: resolvePaymentMethod(record.method),
        });
        setFiles(record.attachment || []);
      } catch (err) {
        showSnackbar({
          message: I18n.unable_to_load_this_entry,
          severity: "error",
        });
        navigate("/Sales/CashBook");
      } finally {
        if (isMounted) setIsPageLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const contactList = useMemo(
    () =>
      (customers || []).map((item) => ({
        id: item.id,
        labelText: item.name,
        label: (
          <ParagraphText className={styles.contactLabel}>
            <span className="newFontColor">{item.name}</span>
            <span className="newLightColor">
              {item?.is_supplier ? I18n.supplier : I18n.customer}
            </span>
          </ParagraphText>
        ),
        value: item.name,
      })),
    [customers, I18n],
  );

  const top2Cate = categoryList.slice(0, 2);

  const openCreateCustomerModal = (value) => {
    if (value?.match?.(phoneNumRegex) === null) {
      setInitname(value);
      setInitphone("");
    } else {
      setInitphone(value);
      setInitname("");
    }
    setCreateCustomerModal(true);
  };

  const handleCreateSupplier = (name) => openCreateCustomerModal(name);

  const handleAddCustomer = (payload) => {
    setCreateCustomerModal(false);
    setState((e) => ({
      ...e,
      customer: payload
        ? {
          id: payload.id,
          label: payload.name,
          labelText: payload.name,
          value: payload.name,
        }
        : null,
    }));
  };

  const handleViewQR = (bool) => {
    if (bool) triggerLogEvent(analytics, "cash_book_qr_viewed");
    setOpen(bool);
  };

  const handleGetQR = () => {
    setTimeout(() => navigate("/WalletOnboarding"), 100);
  };

  const handleAddContact = () => setAddContact(true);

  const handleSendMoney = () => {
    triggerLogEvent(analytics, "cash_book_send_money_clicked");
    if (state.amount === "" || state.amount == null || Number(state.amount) <= 0) {
      showSnackbar({
        message: I18n.amount_required_to_send_money,
        severity: "error",
      });
      return;
    }
    if (!isEMIVerified) {
      setTimeout(() => navigate("/WalletOnboarding"), 100);
      return;
    }
    const isVerifiedAccountForWallet =
      accountInfo?.emi_onboarding_status === EMIONBOARDINGSTATUS.VERIFIED &&
      accountInfo?.verified === VERIFIED.VERIFIED;

    const expensePayload = {
      amount: Number(state.amount) || 0,
      subTotal: Number(state.amount) || 0,
      discount: 0,
      discountUnit: "rs",
      taxRate: 0,
      note: state.note,
      category: state.category,
      date: state.date?.toISOString
        ? state.date.toISOString()
        : new Date().toISOString(),
      status: "Paid",
      customerId: state.customer?.id || null,
      items: [],
      type,
      files,
      source: "cash_book",
    };

    setTimeout(
      () =>
        navigate(isVerifiedAccountForWallet ? "/UCash/SendMoney" : "/WalletOnboarding", {
          state: { expensePayload, backPath: "/Sales/CashBook" },
        }),
      100,
    );
  };

  const isDisplayRemainingSection =
    (state.amount !== "" && state.amount != null && Number(state.amount) > 0) ||
    isEditMode;
  const isDisabled =
    state.amount === "" ||
    state.amount == null ||
    Number(state.amount) <= 0 ||
    isLoading;

  const onSave = async () => {
    if (state.amount === "" || state.amount == null || Number(state.amount) <= 0) {
      setError({ text: I18n.invalid_amount, show: true });
      return;
    }
    setIsLoading(true);
    try {
      const amount = Number(state.amount);
      const pmtType = state.paymentMethod?.type || state.paymentMethod?.name;
      const pmtName = state.paymentMethod?.name;
      const customerRecord = state.customer?.id
        ? await findCustomerByID(state.customer.id)
        : null;
      const nextInvoiceCount = Number(business?.invoice_number_count || 0) + 1;

      const details = {
        amount,
        sales_amount: isMoneyIn ? amount : 0,
        cost_amount: isMoneyIn ? 0 : amount,
        total: amount,
        sub_total: amount,
        discount: 0,
        discount_unit: "",
        tax_title: "",
        tax_rate: 0,
        advance_amount: 0,
        invoice_title: "",
        invoice_number:
          isEditMode && cashTxnDetail
            ? cashTxnDetail.invoice_number
            : `${formattedCount(nextInvoiceCount)}`,
        invoice_prefix:
          isEditMode && cashTxnDetail
            ? cashTxnDetail.invoice_prefix
            : isMoneyIn
              ? "IN#"
              : "OUT#",
        signature: null,
        profit: 0,
        attachment: files,
        date: state.date || new Date(),
        image_url: "",
        document_url: "",
        note: state.note,
        status: "paid",
        tranType: "cash",
        method: pmtType || "cash",
        type,
        bank: pmtType === "bank" ? pmtName || "bank" : "",
        wallet: "",
        category: state.category,
        customer: customerRecord,
      };

      if (isEditMode && cashTxnDetail) {
        await cashTxnDetail.updateCashTxn(details);
      } else {
        await business.createCashTxn(details);
        await business.updateBusinessInvoiceNo(nextInvoiceCount);
      }
      Sync(user?.phone_number, navigate);
      triggerLogEvent(
        analytics,
        isMoneyIn ? "cash_book_money_in_saved" : "cash_book_money_out_saved",
      );
      showSnackbar({ message: I18n.saved_successfully, severity: "success" });
      navigate("/Sales/CashBook");
    } catch (err) {
      showSnackbar({
        message: err?.message || I18n.something_went_wrong,
        severity: "error",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isPageLoading || isAccountLoading) return <BusinessLoader loadBusiness />;

  return (
    <Grid
      container
      className={`${styles.root} ${styles.SaleExpenseFormWrapper} CashTxnForm`}
    >
      <Grid item md={12}>
        <TitleAndActionBox
          data={{
            title: isMoneyIn ? I18n.new_entry_in : I18n.new_entry_out,
            isTitleCase: false,
            isBack: true,
            onBack: isLoading ? () => { } : () => navigate("/Sales/CashBook"),
            id: "cash_txn_form",
          }}
        />

        <Box id="cash_txn_form_body" className={styles.formBodyGrid}>
          <Box id="cash_txn_form_column" className={styles.formColumn}>
            <Box id="cash_txn_form_fields" className={`${styles.formBody} expenseFormBody`}>

              <Box id="cash_txn_amount_box" className={styles.expenseAmountInputBox}>
                <CalcInput
                  inputId={isMoneyIn ? "money_in_amount" : "money_out_amount"}
                  baseClassname={styles.amountInput}
                  placeholder={I18n.amount}
                  onChange={(_, value) => {
                    setError("");
                    setState((e) => ({ ...e, amount: value }));
                  }}
                  label={I18n.enter_amount_trans}
                  name={"amount"}
                  error={error}
                  required
                  value={state.amount}
                  inputRef={inputAmountRef}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !isDisabled) onSave();
                  }}
                />
              </Box>

              {isDisplayRemainingSection ? (
                <Fragment>
                  <Box id="cash_txn_payment_section" className={styles.paymentStatusBoxWrapper}>
                    <Box id="cash_txn_payment_box" className={styles.paymentStatusBox}>
                      <Box
                        id="cash_txn_payment_methods"
                        className={styles.paymentStatusBoxLeft}
                        sx={{ gap: "16px" }}
                      >
                        {paymentOptions.map((item) => (
                          <UBRadioBox
                            key={item.name}
                            label={item.display_name}
                            isLabelStatic
                            value={state.paymentMethod?.display_name}
                            onChange={(label) => {
                              const method =
                                paymentOptions.find((m) => m.display_name === label) ||
                                null;
                              triggerLogEvent(analytics, "cash_book_payment_method_changed", { method: method?.type || label });
                              setState((e) => ({ ...e, paymentMethod: method }));
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    <AppCollectPayment
                      visible={true}
                      status={"Paid"}
                      hasRaastDetail={!!isEMIVerified}
                      onViewQR={() => handleViewQR(true)}
                      onGetQR={() => handleGetQR()}
                      onAddContact={isMoneyIn ? handleAddContact : handleSendMoney}
                      customMessage={!isMoneyIn ? I18n.make_payment_with_bank_transfer : null}
                      customLinkText={!isMoneyIn ? I18n.send_money : null}
                      contactValue={state?.customer?.labelText || ""}
                      isSale={isMoneyIn}
                    />
                  </Box>

                  <Grid
                    container
                    spacing={2}
                    className={`${styles.formBodyGridRow} ${styles.GridRowCategoryNotes}`}
                  >
                    <Grid item md={12}>
                      <Box
                        id="cash_txn_category_box"
                        className={`${styles.tabAutoCompleteBox} ${styles.categoryAutoComplete} tabAutoCompleteBox categoryAutoComplete`}
                      >
                        <TabAutoComplete
                          id={isMoneyIn ? "money_in_category" : "money_out_category"}
                          list={categoryList}
                          label={I18n.category_pl_tracking}
                          handleSelect={(item) => {
                            setState((e) => ({
                              ...e,
                              category: item?.id || "",
                              categoryLabel: item?.label || "",
                            }));
                          }}
                          value={state.categoryLabel}
                          placeholder={`${I18n.category} (${I18n.add_category_to_track_PAndL})`}
                          handleStartLogEvent={() =>
                            triggerLogEvent(
                              analytics,
                              isMoneyIn
                                ? "cash_book_money_in_category_start"
                                : "cash_book_money_out_category_start",
                            )
                          }
                          handleManuallyInput={() => setCategoryOpen(false)}
                          manuallyFocusInput={categoryOpen}
                        />

                        <Box id="cash_txn_category_chips" className={styles.categoryInputBoxChip}>
                          {top2Cate.length > 1 &&
                            top2Cate.map((item) => (
                              <Chip
                                id={`cash_book_category_chip_${(item.label || "").replace(/\s+/g, "_").toLowerCase()}`}
                                onClick={() =>
                                  setState((e) => ({
                                    ...e,
                                    category: item?.id || "",
                                    categoryLabel: item?.label || "",
                                  }))
                                }
                                className={styles.categoryInputBoxChipItem}
                                label={item.label}
                              />
                            ))}
                          <span
                            id="cash_book_category_see_more"
                            onClick={() => setCategoryOpen(true)}
                            className="cursorPointer barclayBlueColor fontSize12 textDecorationUnerline"
                          >
                            {I18n.see_more}
                          </span>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item md={12}>
                      <Box id="cash_txn_note_box" className={styles.noteInputBox}>
                        <UBInput
                          inputId={isMoneyIn ? "money_in_note" : "money_out_note"}
                          name="note"
                          label={I18n.notes}
                          placeholder={I18n.note_placeholder}
                          onChange={(_, value) =>
                            setState((e) => ({ ...e, note: value }))
                          }
                          value={state.note}
                          multiline
                          rows={1}
                          maxLength={500}
                          baseClassname={styles.noteInput}
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  <Box id="cash_txn_contact_box" className={`${styles.tabAutoCompleteBox} tabAutoCompleteBox`}>
                    <TabAutoComplete
                      id="cash_txn_contactlist"
                      list={contactList}
                      label={I18n.contact}
                      handleSelect={(item) => {
                        setState((e) => ({ ...e, customer: item || null }));
                      }}
                      value={state?.customer?.labelText || ""}
                      handleCreate={handleCreateSupplier}
                      asCreate={"contact"}
                      placeholder={I18n.select_a_contact}
                      handleStartLogEvent={() =>
                        triggerLogEvent(analytics, "cash_book_contact_start")
                      }
                      manuallyFocusInput={addContact}
                      handleManuallyInput={() => setAddContact(false)}
                    />
                  </Box>

                  <Box id="cash_txn_attachment_box" className={styles.attachmentAndDateStyle}>
                    <FileAttachment
                      id="cash_txn_file"
                      filesPath={files}
                      setFilesPath={setFiles}
                      handleLogEvent={() =>
                        triggerLogEvent(analytics, "cash_book_photo_start")
                      }
                      showDate
                      selectedDate={state.date}
                      setSelectedDate={(date) => setState((e) => ({ ...e, date }))}
                      datePlaceholder={I18n.select_date}
                      isEditMode={isEditMode}
                      handleDateLogEvent={() =>
                        triggerLogEvent(analytics, "cash_book_date_start")
                      }
                    />
                  </Box>
                </Fragment>
              ) : null}

              <UBButton
                id="cash_txn_save_btn"
                title={<ParagraphText id="cash_txn_save_label">{I18n.save}</ParagraphText>}
                disabled={isDisabled}
                loading={isLoading}
                className={`buttonPrimary saveSaleButton`}
                onClick={onSave}
              />
            </Box>
          </Box>
          <Box id="cash_txn_qr_column" className={`${styles.qrColumn}`}>
            {open && (
              <Box id="cash_txn_qr_card" className={styles.qrCardAnimated}>
                <PaymentQRCode
                  accountInfo={accountInfo}
                  handleClose={() => setOpen(false)}
                  isModal={true}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Grid>

      <AddCustomerModal
        isOpen={createCustomerModal}
        handleClose={() => setCreateCustomerModal(false)}
        navigate={navigate}
        initialName={initName}
        initialPhone={initPhone}
        onSaveCallback={handleAddCustomer}
        title={
          <ParagraphText id="cash_txn_add_customer_title" className={"ms-1 textLowercase"}>
            {I18n.customer}
          </ParagraphText>
        }
        isTypeField={true}
        handleLogEventCustomerCreated={() =>
          triggerLogEvent(analytics, "cash_book_customer_created")
        }
        handleLogEventCustomerUpdate={() =>
          triggerLogEvent(analytics, "customer_data_updated")
        }
      />
    </Grid>
  );
};

const enhance = withObservables([], () => ({
  customers: getCustomerOfBusiness(localStorage.getItem("business_id") || ""),
}));

export default enhance(CashTxnForm);
